import { useCallback } from "react";

import { useLocale } from "@superweb/intl";
import { useFlag } from "@superweb/flags";

import { useOnDemandV1ParkInfoGet } from "@fleet/api/fleet-forms";

import { useRefId } from "#internal/router";
import { useOperationIdContext } from "#internal/ui";
import { useParkInfoContext } from "#internal/park-info-context";

import { sendGtagEvent } from "./gtag";
import { sendFBQEvent } from "./fbq";
import { sendYMEvent, sendYMHit } from "./metrika";
import { sendTTQEvent } from "./ttq";

export const useHitParams = () => {
  const refId = useRefId();
  const locale = useLocale();

  const { sessionId } = useOperationIdContext();

  const flag = useFlag();
  const enableParkInfoFromContext = flag({
    id: "72f8763d-3302-406b-aa9e-7953ad084043",
    description: "Enable park info from context",
  });

  const parkInfoFromContext = useParkInfoContext();

  const { data: parkInfoDeprecated } = useOnDemandV1ParkInfoGet(
    {
      headers: {
        language: locale.toString(),
        "x-session-id": sessionId,
      },
      query: { ref_id: refId },
    },
    {
      enabled: !enableParkInfoFromContext && Boolean(refId),
    },
  );

  const parkInfo = enableParkInfoFromContext
    ? parkInfoFromContext
    : parkInfoDeprecated?.status === 200
      ? { status: 200 as const, body: parkInfoDeprecated.data }
      : {};

  return {
    ref_id: refId,
    lang: locale.toString(),
    ...(parkInfo?.status === 200 && {
      park_brand: parkInfo.body.brand,
      channel_name: parkInfo.body.channel_name,
      channel_id: parkInfo.body.channel_id,
      park_name: parkInfo.body.partner_name,
    }),
  };
};

type StageDeprecated =
  | "main"
  | "vehicle"
  | "license"
  | "download_app"
  | "archive";
type Stage = "register" | "vehicle" | "details" | "info" | "download_app";

type ContactActions = {
  phone_click: {
    stage: StageDeprecated;
  };
  messenger_click: { stage: StageDeprecated };
  phone_click_v2: {
    stage: Stage;
  };
  messenger_click_v2: { stage: Stage };
};

type SumbitActions = {
  submit_form: "auth" | "no_auth";
  submit_vehicle: undefined;
  submit_license: undefined;
  download_app: undefined;
};

type ValidationActions = {
  invalid_phone_number: undefined;
  invalid_driver_license: undefined;
  invalid_car_number: undefined;
  invalid_issue_date: undefined;
  invalid_expiry_date: undefined;
};

type MultiProfessionFormActions = {
  profession_click: {
    profession: string;
  };
  employment_type_click: {
    employment_type: string;
  };
};

type TaxSystemActions = {
  tax_system_osno_banner_shown: undefined;
  tax_system_osno_banner_clicked: undefined;
  tax_system_change: {
    from: string;
    to: string;
  };
};

type ActionsGoal = {
  submit: SumbitActions;
  invalid: ValidationActions;
  contact: ContactActions;
  multi_profession_form: MultiProfessionFormActions;
  tax_system: TaxSystemActions;
};

export const useLogAction = <T extends keyof ActionsGoal>(group: T) => {
  const hitParams = useHitParams();

  return useCallback(
    <A extends keyof ActionsGoal[T]>(
      action: A,
      options?: { onlyYM?: boolean; params?: ActionsGoal[T][A] },
    ) => {
      if (!options?.onlyYM) {
        sendGtagEvent(action as string, `form_${hitParams.ref_id}`);
        sendFBQEvent(action as string, `form_${hitParams.ref_id}`);
        sendTTQEvent(action as string, `form_${hitParams.ref_id}`);
      }
      sendYMEvent(action as string, group, hitParams, options?.params);
    },
    [group, hitParams],
  );
};

export const useLogHit = () => {
  const hitParams = useHitParams();

  return useCallback(
    (url: string) => {
      sendYMHit(url, hitParams);
    },
    [hitParams],
  );
};
